import { Alert, AppShell, Box, Group, ScrollArea, SegmentedControl, Text, Tooltip } from '@mantine/core';
import { IconUser, IconBuilding } from '@tabler/icons-react';
import cx from 'clsx';
import { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useBot, useCanAccess, useCollaborateStore, useMedia } from '@mygpt/app';
import Avatar from '@/components/Headers/Avatar';
import Language from '@/components/Headers/Language';
import { SvgBack } from '@/components/Navbar/icon';
import { SvgTypeDocument, SvgTypeWebsite } from '@/components/Robot/Item/icon';
import { useGPTStore } from '@/store';
import { useNavStore } from '@/store/nav';
import { NavItem as T_NavItem } from '@/types/nav-item';
import { DatasetNav } from './dataset';
import classes from './index.module.css';
import NavItem from './item';
import { useAdminStore } from '@/store/admin';
import { adminConsoleLinks, collaborationLink, securityPolicyLink } from '@/config/links';
import { AdminLogo } from '@/components/Navbar/AdminLogo';

interface Props {
  data: T_NavItem[];
}

export default ({ data }: Props) => {
  const { t } = useTranslation();
  const { botId } = useBot();
  const { hasAdminConsoleAuthority, companyId, companyName, userOrgEnabled } = useAdminStore((state) => state);
  const { isMobile } = useMedia();
  const { setNavbarOpened } = useNavStore();
  const { isAssist, setIsAssist } = useCollaborateStore();
  const canAccessCollaboration = useCanAccess('enable_collaboration');
  const { robotInfo, quotaInfo, roles, assetScope, setAssetScope } = useGPTStore();
  const isLocalMode = import.meta.env.VITE_MODE === 'local';
  const isCloudMode = !isLocalMode;

  const navigate = useNavigate();
  const location = useLocation();
  useLayoutEffect(() => {
    const isAssistPath = location.pathname.includes('/assist');
    setIsAssist(isAssistPath);
  }, [location]);

  // Cloud 模式下的状态判断
  const inPersonalMode = isCloudMode && assetScope === null;
  const inOrgMode = isCloudMode && assetScope !== null;
  const inOrgModeWithOrg = inOrgMode && !!companyId;
  const inOrgModeNoOrg = inOrgMode && !companyId;
  const isViewer = roles && Array.isArray(roles) && roles.some((r: any) => r.code === 'ROLE_VIEWER');
  const canManageSecurityPolicy =
    hasAdminConsoleAuthority &&
    roles &&
    Array.isArray(roles) &&
    roles.some((role: any) => role.code === 'ROLE_COMPANY_OWNER' || role.code === 'ROLE_ADMIN');

  // 动态权限过滤逻辑
  const filteredData = useMemo(() => {
    // 权限过滤功能只在本地部署版生效
    if (isLocalMode && roles && Array.isArray(roles) && roles.some((r: any) => r.code === 'ROLE_VIEWER')) {
      return data.map((group) => ({
        ...group,
        item: group.item.filter((item: any) => item.link === '/bots'),
      }));
    }

    // Cloud 企业模式下隐藏「协同空间」（与 onprem 行为一致）
    if (inOrgMode) {
      return data.map((group) => ({
        ...group,
        item: group.item.filter((item: any) => {
          if (item.link === '/collaborative') return false;
          // 企业模式下 VIEWER 隐藏知识库菜单
          if (isViewer && item.link === '/datasets') return false;
          return true;
        }),
      }));
    }

    return data;
  }, [data, roles, inOrgMode, isViewer]);

  // 判断是否显示导航项的逻辑
  const shouldShowNavigation = useMemo(() => {
    if (isLocalMode) {
      // 本地部署版：等待roles数据加载完成
      return roles && roles.length > 0;
    } else {
      // 线上版本：直接显示
      return true;
    }
  }, [roles]);

  // 企业模式+无企业 或 用户被禁用时隐藏所有菜单（上方导航+下方链接）
  const hideAllMenus = inOrgModeNoOrg || (inOrgModeWithOrg && userOrgEnabled === false);

  const links =
    shouldShowNavigation && !hideAllMenus
      ? filteredData.map((link, index) => {
          return (
            <Box key={index}>
              {link.group && (
                <Text color="gray.6" mx={12} my={10} size={'xs'}>
                  {t(link.group)}
                </Text>
              )}
              {link.item.map((item, index) =>
                quotaInfo ? <NavItem key={index} {...item} quotaInfo={quotaInfo} /> : null
              )}
            </Box>
          );
        })
      : [];

  const bottomLinks = () => {
    if (hideAllMenus) return [];

    const links = [];

    if (!botId && canAccessCollaboration) {
      if (isLocalMode) {
        // === 本地部署版逻辑（保持不变） ===
        if (hasAdminConsoleAuthority) {
          links.push(...adminConsoleLinks);
        }
        if (canManageSecurityPolicy) {
          links.push(securityPolicyLink);
        }
        if (!(roles && Array.isArray(roles) && roles.some((r: any) => r.code === 'ROLE_VIEWER'))) {
          links.push(collaborationLink);
        }
      } else {
        // === Cloud 版逻辑 ===
        if (inPersonalMode) {
          // 个人模式：隐藏「企业权限管理」，显示「访问管理」（VIEWER 除外）
          if (!isViewer) {
            links.push(collaborationLink);
          }
        } else if (inOrgModeWithOrg) {
          // 企业模式 + 有企业：按角色控制
          if (hasAdminConsoleAuthority) {
            links.push(...adminConsoleLinks);
          }
          if (canManageSecurityPolicy) {
            links.push(securityPolicyLink);
          }
          if (!isViewer) {
            links.push(collaborationLink);
          }
        }
      }
    }

    return links;
  };

  const [modeSwitching, setModeSwitching] = useState(false);

  // Cloud 模式切换处理，切换后回到首页
  const handleModeSwitch = async (value: string) => {
    if (value === 'personal') {
      setAssetScope(null);
      navigate('/bots');
    } else {
      let id = companyId;
      if (!id) {
        // companyId 尚未加载完成，等待 fetchCompanyId 返回
        setModeSwitching(true);
        try {
          await useAdminStore.getState().fetchCompanyId();
          id = useAdminStore.getState().companyId;
        } catch {
          // 获取失败，用户可能未加入组织
        } finally {
          setModeSwitching(false);
        }
      }
      // 只在获取到有效 companyId 时才设置，否则设为空字符串触发 EnableOrganizationView
      setAssetScope(id || '');
      navigate('/bots');
    }
  };

  useEffect(() => {
    setNavbarOpened(!isMobile);
  }, []);

  const currentMode = assetScope === null ? 'personal' : 'enterprise';

  return (
    <>
      <AppShell.Section className="relative">
        {botId ? (
          <div className="p-5">
            <NavLink to={isAssist ? '/collaborative' : '/bots'}>
              <div className="flex items-center gap-1 cursor-pointer text-gray-800 hover:text-violet-500 transition-all">
                <SvgBack />
                <Text fz="md">{isAssist ? t('menu.exit') : t('menu.my_bots')}</Text>
              </div>
            </NavLink>

            <Group gap={12} mt={16} mb={4} wrap="nowrap">
              <div className="w-[40px] h-[40px] flex justify-center rounded-md">
                <div className="scale-[60%] -translate-y-1.5">
                  {robotInfo?.ai_model === 'website' ? <SvgTypeWebsite /> : <SvgTypeDocument />}
                </div>
              </div>
              <div className="flex flex-col gap-2 justify-around overflow-hidden">
                <Tooltip label={robotInfo?.name} withArrow multiline>
                  <p className="text-gray-800 truncate">{robotInfo?.name}</p>
                </Tooltip>
                <Tooltip label={robotInfo?.discrible} withArrow multiline>
                  <p className="text-gray-500 text-sm font-[300] truncate">{robotInfo?.discrible}</p>
                </Tooltip>
              </div>
            </Group>
          </div>
        ) : (
          <AdminLogo className="my-[40px]" />
        )}
      </AppShell.Section>

      <AppShell.Section grow component={ScrollArea}>
        <Box mt={0} mb={20} px={12}>
          {links}
          {botId && <DatasetNav />}
        </Box>
      </AppShell.Section>

      <AppShell.Section className="px-3 py-6">
        {/* 用户被禁用提示 */}
        {isCloudMode && inOrgModeWithOrg && userOrgEnabled === false && (
          <Alert color="red" variant="light" className="mb-2" p="xs">
            <Text size="xs">{t('org.accountDisabled', '您的企业账号已被禁用，请联系管理员')}</Text>
          </Alert>
        )}

        {/* 底部菜单链接 */}
        {!isAssist &&
          shouldShowNavigation &&
          bottomLinks().map((link, index) => (
            <Box key={index}>{quotaInfo ? <NavItem key={index} {...link} quotaInfo={quotaInfo} /> : null}</Box>
          ))}

        {/* Cloud 模式切换：个人 / 企业（位于底部菜单与语言工具栏之间） */}
        {isCloudMode && !botId && !isAssist && (
          <div className="mb-3 mt-1">
            <SegmentedControl
              value={currentMode}
              onChange={handleModeSwitch}
              disabled={modeSwitching}
              fullWidth
              size="xs"
              data={[
                {
                  value: 'personal',
                  label: (
                    <div className="flex items-center justify-center gap-1.5 py-0.5">
                      <IconUser size={14} />
                      <span>{t('mode.personal', '我的空间')}</span>
                    </div>
                  ),
                },
                {
                  value: 'enterprise',
                  label: (
                    <div className="flex items-center justify-center gap-1.5 py-0.5">
                      <IconBuilding size={14} />
                      <span className="truncate">{companyName || t('mode.organization', '企业')}</span>
                    </div>
                  ),
                },
              ]}
              styles={{
                root: {
                  backgroundColor: 'var(--mantine-color-gray-1)',
                  borderRadius: '8px',
                  padding: '2px',
                },
                indicator: {
                  borderRadius: '6px',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                },
                label: {
                  fontSize: '12px',
                  fontWeight: 500,
                  padding: '4px 8px',
                },
              }}
            />
          </div>
        )}

        <div className={cx('p-3 flex justify-between align-center rounded-full bg-white', classes.navInfo)}>
          <Language />
          <Avatar />
        </div>
      </AppShell.Section>
    </>
  );
};
